import { useEffect, useState, type FormEvent } from 'react';
import { setSessionToken, setTokenExpiry } from '@room/frontend-sdk';
import { LayoutFrame, registerHomeTile, type APIResponse } from '@room/shared-ui';
import { api } from '../lib/api';
import { useCreditsStore, triggerCreditUpdate } from '../stores/credits';
import './CreditsPage.css';

registerHomeTile({
  id: 'home-wallet',
  label: 'Wallet',
  icon: '💰',
  route: '/credits',
  section: 'dock',
  order: 20,
});

type CreditBalance = {
  balance: number;
  total_deposited: number;
  total_spent: number;
  total_received: number;
  updated_at: number;
};

type CreditTransactions = {
  transactions: Array<{
    id: string;
    amount: number;
    transaction_type: string;
    created_at: number;
    description?: string;
  }>;
};

type PiPaymentCallbacks = {
  onReadyForServerApproval: (paymentId: string) => void | Promise<void>;
  onReadyForServerCompletion: (paymentId: string, txid: string) => void | Promise<void>;
  onCancel: () => void;
  onError: (err: Error) => void;
};

type PiPaymentRequest = {
  amount: number;
  memo: string;
  metadata: Record<string, unknown>;
};

type AuthResponse = {
  sessionToken: string;
  expiresAt?: number;
};

type PiSdk = {
  init: (options: { version: string; sandbox: boolean }) => Promise<void>;
  authenticate?: (scopes: string[], onIncompletePaymentFound: (payment: unknown) => void) => Promise<{
    accessToken: string;
    user: unknown;
  }>;
  createPayment?: (request: PiPaymentRequest, callbacks: PiPaymentCallbacks) => void;
};

type PiSdkWindow = Window & {
  Pi?: PiSdk;
  __PI_SDK_READY__?: boolean;
  __PI_SDK_INIT_PROMISE__?: Promise<void>;
};

const PI_SDK_URL = 'https://sdk.minepi.com/pi-sdk.js';
const PI_SDK_OPTIONS = { version: '2.0', sandbox: false };

const loadPiSdkScript = () =>
  new Promise<void>((resolve, reject) => {
    const existingPi = (window as PiSdkWindow).Pi;
    if (existingPi) {
      resolve();
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>('script[data-pi-sdk="true"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve());
      existingScript.addEventListener('error', () => reject(new Error('Pi SDK failed to load.')));
      return;
    }

    const script = document.createElement('script');
    script.src = PI_SDK_URL;
    script.async = true;
    script.defer = true;
    script.dataset.piSdk = 'true';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Pi SDK failed to load.'));
    document.head.appendChild(script);
  });

const ensurePiSdkReady = async () => {
  const piWindow = window as PiSdkWindow;
  if (piWindow.__PI_SDK_READY__) {
    return;
  }

  if (!piWindow.__PI_SDK_INIT_PROMISE__) {
    piWindow.__PI_SDK_INIT_PROMISE__ = (async () => {
      await loadPiSdkScript();
      if (!piWindow.Pi?.init) {
        piWindow.__PI_SDK_READY__ = false;
        throw new Error('Pi SDK not available. Please use Pi Browser.');
      }
      await piWindow.Pi.init(PI_SDK_OPTIONS);
      piWindow.__PI_SDK_READY__ = true;
    })();
  }

  await piWindow.__PI_SDK_INIT_PROMISE__;
};

export function CreditsPage() {
  const { fullData: balance, fetchCredits, loading: creditsLoading } = useCreditsStore();
  const [transactions, setTransactions] = useState<CreditTransactions['transactions']>([]);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [depositAmount, setDepositAmount] = useState('');
  const [transferRecipientId, setTransferRecipientId] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [transferMessage, setTransferMessage] = useState('');
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDepositing, setIsDepositing] = useState(false);
  const [isReauthing, setIsReauthing] = useState(false);
  const [needsPaymentScope, setNeedsPaymentScope] = useState(false);
  const [piSdkUnavailable, setPiSdkUnavailable] = useState(false);

  const isLoading = creditsLoading || isLoadingTransactions;
  const transactionLabels: Record<string, string> = {
    deposit: 'Deposit',
    transfer_in: 'Received',
    transfer_out: 'Sent',
    gift_in: 'Gift received',
    gift_out: 'Gift sent',
    award: 'Reward',
    deduct: 'Charge',
  };
  const formatTimestamp = (timestamp?: number) => {
    if (!timestamp) {
      return null;
    }
    const normalized = timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp;
    return new Date(normalized).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoadingTransactions(true);
      setError(null);
      try {
        await fetchCredits();

        const txResponse = await api.get<APIResponse<CreditTransactions>>('/credits/transactions');
        if (!txResponse.success) {
          throw new Error(txResponse.error || 'Failed to load transactions');
        }
        setTransactions(txResponse.data?.transactions ?? []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setIsLoadingTransactions(false);
      }
    };

    void loadData();
  }, [fetchCredits]);

  const refreshCredits = async () => {
    setIsRefreshing(true);
    try {
      await fetchCredits();
      const txResponse = await api.get<APIResponse<CreditTransactions>>('/credits/transactions');
      if (txResponse.success) {
        setTransactions(txResponse.data?.transactions ?? []);
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleDeposit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setActionMessage(null);
    setNeedsPaymentScope(false);
    try {
      setPiSdkUnavailable(false);
      await ensurePiSdkReady();
      const piSdk = (window as PiSdkWindow).Pi;
      if (!piSdk?.createPayment) {
        throw new Error('Pi SDK not available. Please use Pi Browser.');
      }
      setPiSdkUnavailable(false);
      const amount = parseFloat(depositAmount);
      if (!amount || amount <= 0) {
        throw new Error('Please enter a valid Pi amount.');
      }

      setIsDepositing(true);
      await new Promise<void>((resolve, reject) => {
        piSdk.createPayment?.(
          {
            amount,
            memo: 'Deposit credits',
            metadata: { action: 'deposit-credits' },
          },
          {
            onReadyForServerApproval: async (paymentId: string) => {
              try {
                const approveResponse = await api.post<APIResponse<unknown>>('/payments/approve-deposit', {
                  paymentId,
                  amount,
                });
                if (!approveResponse.success) {
                  throw new Error(approveResponse.error || 'Payment approval failed');
                }
              } catch (approveError) {
                reject(approveError);
              }
            },
            onReadyForServerCompletion: async (paymentId: string, txid: string) => {
              try {
                const response = await api.post<APIResponse<unknown>>('/credits/deposit', {
                  amount,
                  piPaymentId: paymentId,
                  txid,
                });
                if (!response.success) {
                  throw new Error(response.error || 'Deposit failed');
                }

                setActionMessage('Deposit completed.');
                setDepositAmount('');
                triggerCreditUpdate('deposit', amount);
                await refreshCredits();
                resolve();
              } catch (completeError) {
                reject(completeError);
              }
            },
            onCancel: () => {
              setActionMessage('Deposit cancelled.');
              resolve();
            },
            onError: (err: Error) => {
              reject(err);
            },
          }
        );
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Deposit failed';
      if (message.toLowerCase().includes('pi sdk')) {
        setPiSdkUnavailable(true);
      }
      if (message.toLowerCase().includes('payments') && message.toLowerCase().includes('scope')) {
        setNeedsPaymentScope(true);
      }
      setError(message);
    } finally {
      setIsDepositing(false);
    }
  };

  const handleReauth = async () => {
    setIsReauthing(true);
    setError(null);
    setActionMessage(null);
    setNeedsPaymentScope(false);
    try {
      setPiSdkUnavailable(false);
      await ensurePiSdkReady();
      const piSdk = (window as PiSdkWindow).Pi;
      if (!piSdk?.authenticate) {
        throw new Error('Pi SDK not available. Please use Pi Browser.');
      }
      const authResult = await piSdk.authenticate(['username', 'payments', 'wallet_address'], () => {});
      const response = await api.post<APIResponse<AuthResponse>>('/auth/pi', {
        accessToken: authResult.accessToken,
        user: authResult.user,
      });
      if (!response.success || !response.data?.sessionToken) {
        throw new Error(response.error || 'Re-authentication failed');
      }
      setSessionToken(response.data.sessionToken);
      if (response.data.expiresAt) {
        setTokenExpiry(response.data.expiresAt);
      }
      setNeedsPaymentScope(false);
      setActionMessage('Re-authenticated. Please retry the deposit.');
      await refreshCredits();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Re-authentication failed';
      if (message.toLowerCase().includes('pi sdk')) {
        setPiSdkUnavailable(true);
      }
      setError(message);
    } finally {
      setIsReauthing(false);
    }
  };

  const isDepositDisabled = isDepositing || isReauthing || piSdkUnavailable || needsPaymentScope;

  const handleTransfer = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setActionMessage(null);
    try {
      const amount = parseFloat(transferAmount);
      const response = await api.post<APIResponse<unknown>>('/credits/transfer', {
        amount,
        recipientId: transferRecipientId,
        message: transferMessage,
      });
      if (!response.success) {
        throw new Error(response.error || 'Transfer failed');
      }
      setActionMessage('Transfer completed.');
      setTransferAmount('');
      setTransferRecipientId('');
      setTransferMessage('');

      triggerCreditUpdate('transfer', -amount);
      await refreshCredits();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Transfer failed');
    }
  };

  return (
    <LayoutFrame className="credits-page credits-redesign" narrow>
      {isLoading && (
        <div className="wallet-loading">
          <div className="wallet-loading-icon">⏳</div>
          <p className="wallet-loading-text">Loading wallet…</p>
        </div>
      )}

      {!isLoading && (
        <>
          {error && (
            <div className="alert alert-error wallet-alert">
              <span className="wallet-alert-message content-wrap">{error}</span>
              {needsPaymentScope && (
                <button
                  type="button"
                  className="btn btn-primary wallet-alert-button"
                  onClick={handleReauth}
                  disabled={isReauthing}
                >
                  {isReauthing ? 'Re-authenticating…' : 'Re-auth with Pi'}
                </button>
              )}
            </div>
          )}
          {actionMessage && (
            <div className="alert alert-success wallet-alert">
              <span className="wallet-alert-message content-wrap">{actionMessage}</span>
            </div>
          )}

          {balance && (
            <div className="wallet-hero-section">
              <div className="wallet-hero-card">
                <div className="wallet-hero-header">
                  <div>
                    <div className="wallet-hero-label">Current balance</div>
                    {balance.updated_at && (
                      <div className="wallet-hero-meta">
                        Updated {formatTimestamp(balance.updated_at)}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="wallet-refresh-button"
                    onClick={refreshCredits}
                    disabled={isRefreshing}
                    aria-label="Refresh credits"
                  >
                    <span className={`wallet-refresh-icon ${isRefreshing ? 'spinning' : ''}`}>↻</span>
                  </button>
                </div>
                <div className="wallet-hero-value">{balance.balance.toFixed(2)} π</div>
                <div className="wallet-hero-subtitle">Available balance</div>
                <div className="wallet-hero-metrics">
                  <div className="wallet-hero-metric">
                    <span className="wallet-hero-metric-label">Deposited</span>
                    <span className="wallet-hero-metric-value">{balance.total_deposited.toFixed(2)}</span>
                  </div>
                  <div className="wallet-hero-metric">
                    <span className="wallet-hero-metric-label">Spent</span>
                    <span className="wallet-hero-metric-value">{balance.total_spent.toFixed(2)}</span>
                  </div>
                  <div className="wallet-hero-metric">
                    <span className="wallet-hero-metric-label">Received</span>
                    <span className="wallet-hero-metric-value">{balance.total_received.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="wallet-section">
            <div className="wallet-section-header">
              <h3>Transactions</h3>
              <span className="wallet-section-subtitle">Latest credits activity</span>
            </div>
            <div className="wallet-transactions">
              {transactions.length === 0 ? (
                <div className="wallet-empty-state">
                  <div className="wallet-empty-icon">🧾</div>
                  <p>No transactions yet.</p>
                </div>
              ) : (
                transactions.map((transaction) => (
                  <div key={transaction.id} className="wallet-transaction">
                    <div
                      className={`transaction-icon ${transaction.amount >= 0
                        ? 'wallet-transaction-icon--positive'
                        : 'wallet-transaction-icon--negative'}`}
                    >
                      {transaction.amount >= 0 ? '+' : '-'}
                    </div>
                    <div className="transaction-details">
                      <div className="transaction-type">
                        {transactionLabels[transaction.transaction_type] ?? 'Transaction'}
                      </div>
                      <div className="transaction-date">
                        {transaction.description || 'Room credits'}
                      </div>
                      <div className="transaction-date">
                        {formatTimestamp(transaction.created_at)}
                      </div>
                    </div>
                    <div className={`transaction-amount ${transaction.amount >= 0 ? 'positive' : 'negative'}`}>
                      {transaction.amount >= 0 ? '+' : ''}
                      {transaction.amount.toFixed(2)} π
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="wallet-actions wallet-actions--primary">
            <div className="wallet-actions-surface">
              <div className="wallet-section-header">
                <h3>Deposit Pi</h3>
                <span className="wallet-section-subtitle">Top up your Room wallet.</span>
              </div>
              <form className="wallet-form" onSubmit={handleDeposit}>
                <label className="wallet-form-label">
                  Amount (π)
                  <input
                    className="wallet-form-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={depositAmount}
                    onChange={(event) => setDepositAmount(event.target.value)}
                    placeholder="Enter amount"
                  />
                </label>
                <button className="btn btn-primary" type="submit" disabled={isDepositDisabled}>
                  {isDepositing ? 'Depositing…' : 'Deposit Pi'}
                </button>
                {piSdkUnavailable && (
                  <div className="alert alert-error">Pi SDK not available. Open in Pi Browser.</div>
                )}
              </form>
            </div>
          </div>

          <div className="wallet-actions">
            <div className="wallet-actions-surface">
              <div className="wallet-section-header">
                <h3>Transfer credits</h3>
                <span className="wallet-section-subtitle">Send Pi to another user.</span>
              </div>
              <form className="wallet-form" onSubmit={handleTransfer}>
                <label className="wallet-form-label">
                  Recipient ID
                  <input
                    className="wallet-form-input"
                    type="text"
                    value={transferRecipientId}
                    onChange={(event) => setTransferRecipientId(event.target.value)}
                    placeholder="Enter recipient user ID"
                  />
                </label>
                <label className="wallet-form-label">
                  Amount (π)
                  <input
                    className="wallet-form-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={transferAmount}
                    onChange={(event) => setTransferAmount(event.target.value)}
                    placeholder="Enter amount"
                  />
                </label>
                <label className="wallet-form-label">
                  Message (optional)
                  <input
                    className="wallet-form-input"
                    type="text"
                    value={transferMessage}
                    onChange={(event) => setTransferMessage(event.target.value)}
                    placeholder="Add a note"
                  />
                </label>
                <button className="btn btn-primary" type="submit">
                  Send credits
                </button>
              </form>
            </div>
          </div>
        </>
      )}
    </LayoutFrame>
  );
}
